// Basic
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { cn } from '@/common-utils/libraries/tailwind/utils';
// Store & actions
import actionsSims from '../../api/actionsSims';
// Components
import HeaderTitle from '../../../../../common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '../../../../../common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Entity from '../../../../../common-utils/components/renders/entity/Entity';
import { Modal, ModalBody, ModalContent, ModalTrigger } from '@/common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsAccount, nsSimCard } from '@/app/i18n/namespaces';

/**
 * @param {JSX.Element} trigger - Element that triggers modal opening
 * @returns {JSX.Element}  ModalUpdateSimCard Component
 */
function ModalUpdateSimCard({ trigger, isOpen, setIsOpen, sim, setLaunchRefetch }) {
    // Toast
    const { toast } = useToast();

    // Form
    const formSchema = z.object({
        label: z.string().optional()
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { label: sim.label || '' }
    });

    // State
    const [showSubmitButton, setShowSubmitButton] = useState(true);


    // API Calls
    const onSuccess = () => {
        setShowSubmitButton(false);
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.update_label.success' namespace={nsSimCard} />,
            variant: 'success'
        });
    };

    const onError = () => {
        setShowSubmitButton(false);
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.update_label.error' namespace={nsSimCard} />,
            variant: 'danger'
        });
    };
    const submitUpdateLabel = actionsSims.updateLabel(onSuccess, onError);

    const onSubmit = (data) => {
        submitUpdateLabel.mutate({ uuid: sim.uuid, label: data.label });
    };
    const onClose = () => {
        setIsOpen(false);
        if (submitUpdateLabel.isSuccess) {
            setLaunchRefetch(true);
        }
    };

    // Modal Content
    const content = (
        <ModalContent className={cn('max-w-lg')}>
            <HeaderTitle title='modal.update_label.title' namespace={nsSimCard} />
            <ModalBody>
                <Form {...form} onSubmit={form.handleSubmit(onSubmit)}>
                    <FormLabel htmlFor='label' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.update_label.text' namespace={nsSimCard} />
                    </FormLabel>
                    <FormField
                        name='label'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input'>
                                <FormControl>
                                    <Input
                                        {...field}
                                        autoFocus
                                        type='text'
                                        entity='modal.update_merchant_label.placeholder'
                                        namespace={nsAccount}
                                        autoComplete='off'
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </Form>
            </ModalBody>
            <FooterForm
                onSubmit={form.handleSubmit(onSubmit)}
                isSubmitting={submitUpdateLabel.isLoading}
                showSubmitButton={showSubmitButton}
            />
        </ModalContent>
    );

    return trigger ? (
        <Modal>
            <ModalTrigger>{trigger}</ModalTrigger>
            {content}
        </Modal>
    ) : (
        <Modal open={isOpen} onOpenChange={onClose}>
            {content}
        </Modal>
    );
}
ModalUpdateSimCard.propTypes = {
    trigger: PropTypes.element,
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    sim: PropTypes.shape({
        uuid: PropTypes.string.isRequired,
        label: PropTypes.string
    }).isRequired,
    setLaunchRefetch: PropTypes.func.isRequired
};
export default ModalUpdateSimCard;
